import { createRoute, redirect, useNavigate } from '@tanstack/react-router'
import { appShellRoute } from '@/app/routes/_appShell'
import { parseProjectTab, type ProjectTab } from '@/app/routes/_search'
import { readRememberedTab } from '@/components/ui/Tabs'
import { createFirstVisitRestore, hiddenVisibility, isScope, isSort, isStatus } from '@/features/projects/filters'
import { ProjectsPage, type ProjectsSearch } from '@/features/projects/pages/ProjectsPage'
import { PROJECT_TABS_KEY, ProjectDetailPage } from '@/features/projects/pages/ProjectDetailPage'

const restoreOnFirstVisit = createFirstVisitRestore()

export const indexRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/',
  validateSearch: (raw: Record<string, unknown>): ProjectsSearch => {
    const sort = raw['sort']
    const page = raw['page']
    const channel = raw['channel']
    const out: ProjectsSearch = {}
    if (typeof channel === 'string' && channel) out.channel = channel
    if (isSort(sort)) out.sort = sort
    if (typeof page === 'number' && page > 0) out.page = page
    if (isStatus(raw['status'])) out.status = raw['status']
    if (isScope(raw['scope'])) out.scope = raw['scope']
    if (typeof raw['hide'] === 'string' && raw['hide']) out.hide = raw['hide']
    if (typeof raw['vis'] === 'string') {
      const vis = hiddenVisibility({ vis: raw['vis'] })
      if (vis.length > 0) out.vis = vis.sort().join(',')
    }
    return out
  },
  beforeLoad: ({ search, preload }) => {
    // Um preload (hover num link) não é a visita; não gasta a restauração.
    if (preload) return
    const restored = restoreOnFirstVisit(search)
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- redirect() é o sinal do router
    if (restored) throw redirect({ to: '/', search: restored, replace: true })
  },
  component: IndexRouteComponent,
})

function IndexRouteComponent() {
  const search = indexRoute.useSearch()
  return <ProjectsPage search={search} />
}

export const projectDetailRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: '/projects/$projectId',
  validateSearch: (raw: Record<string, unknown>): ProjectDetailSearch => {
    const out: ProjectDetailSearch = {
      lang: typeof raw['lang'] === 'string' ? raw['lang'] : 'pt-br',
    }
    if (typeof raw['channel'] === 'string' && raw['channel']) out.channel = raw['channel']
    // ``?tab=analytics`` (a aba antes de virar "YouTube") abre a YouTube.
    const tab = parseProjectTab(raw['tab'])
    if (tab) out.tab = tab
    return out
  },
  component: ProjectDetailRouteComponent,
})

interface ProjectDetailSearch {
  lang: string
  channel?: string
  tab?: ProjectTab
}

/** First tab, and the one the URL leaves out. */
const DEFAULT_PROJECT_TAB: ProjectTab = 'info'

function ProjectDetailRouteComponent() {
  const { projectId } = projectDetailRoute.useParams()
  const { lang, tab } = projectDetailRoute.useSearch()
  const navigate = useNavigate({ from: '/projects/$projectId' })
  return (
    <ProjectDetailPage
      projectId={projectId}
      lang={lang}
      tab={tab ?? parseProjectTab(readRememberedTab(PROJECT_TABS_KEY)) ?? DEFAULT_PROJECT_TAB}
      onTabChange={(next) => {
        void navigate({
          search: (prev) => {
            const search: ProjectDetailSearch = { ...prev, tab: next }
            if (search.tab === DEFAULT_PROJECT_TAB) delete search.tab
            return search
          },
        })
      }}
    />
  )
}
